import { useEffect, useState } from 'react';
import { Address } from 'react-daum-postcode';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { usePostSendEmailVerificationApi, usePostVerifyCodeApi } from '@/api/auth';
import { useGetMemberInfoApi, usePostModifyMemberInfoApi } from '@/api/mypage';
import Field from '@/components/common/Field/Field';
import InputAddress from '@/components/common/Input/InputAddress/InputAddress';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import InputText from '@/components/common/Input/InputText/InputText';
import { MYPAGE_RAW_EMAIL_DIRECT_INPUT, MYPAGE_RAW_GENDER_FEMALE, MYPAGE_RAW_GENDER_MALE } from '@/constants/mypage';
import { daysOptions, emailOptions, monthOptions, yearOptions } from '@/constants/option';
import useIsMobile from '@/hooks/useIsMobile';
import { useModalStore } from '@/store/modalStore';
import { useToastStore } from '@/store/toastStore';
import { formatCell } from '@/utils/formatting';

import * as S from './style';

interface RegisterField {
	name: string;
	enName: string;
	birth: { year: string; month: string; day: string };
	gender: string;
	address: { zipcode: string; address: string; detailAddress: string };
	cell: string;
	affiliation: string;
	researchField: string;
	studentNumber: string;
	email: { front: string; back: string; code: string; verificationCode: string };
}
const BasicInfo = () => {
	const { t } = useTranslation();
	const isMobile = useIsMobile(1180);
	const [isOpen, setIsOpen] = useState(false);
	const [isEmailVerified, setIsEmailVerified] = useState(false);
	const [isSendEmail, setIsSendEmail] = useState(false);
	const { showModal } = useModalStore();
	const { openToast } = useToastStore();

	const userRegisterSchema = z.object({
		name: z.string().min(1, { message: 'mypage.basicInfo.validation.name' }),
		enName: z.string().min(1, { message: 'mypage.basicInfo.validation.enName' }),
		birth: z.object({
			year: z.string().min(1, { message: 'mypage.basicInfo.validation.birth' }),
			month: z.string().min(1, { message: 'mypage.basicInfo.validation.birth' }),
			day: z.string().min(1, { message: 'mypage.basicInfo.validation.birth' }),
		}),
		email: z.object({
			front: z.string().min(1, { message: 'mypage.basicInfo.validation.email' }),
			back: z.string().min(1, { message: 'mypage.basicInfo.validation.email' }),
			code: z.string(),
			verificationCode: z.string(),
		}),
		gender: z.string().min(1, { message: 'mypage.basicInfo.validation.gender' }),
		address: z.object({
			zipcode: z.string().min(1, { message: 'mypage.basicInfo.validation.zipcode' }),
			address: z.string().min(1, { message: 'mypage.basicInfo.validation.address' }),
			detailAddress: z.string().min(1, { message: 'mypage.basicInfo.validation.detailAddress' }),
		}),
		cell: z.string().min(1, { message: 'mypage.basicInfo.validation.cell' }),
		affiliation: z.string().min(1, { message: 'mypage.basicInfo.validation.affiliation' }),
		researchField: z.string().min(1, { message: 'education.validation.researchField' }),
		studentNumber: z.string().min(1, { message: 'education.validation.studentNumber' }),
	});
	const {
		register,
		handleSubmit,
		watch,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		mode: 'onChange',
		defaultValues: {
			name: '',
			enName: '',
			birth: { year: '', month: '', day: '' },
			cell: '',
			email: { front: '', back: '', code: MYPAGE_RAW_EMAIL_DIRECT_INPUT, verificationCode: '' },
			gender: MYPAGE_RAW_GENDER_MALE,
			address: { zipcode: '', address: '', detailAddress: '' },
			researchField: '',
			affiliation: '',
			studentNumber: '',
		},
	});
	const { onChange, ...rest } = register('email.verificationCode');
	const { mutateAsync: VerifyCode } = usePostVerifyCodeApi();
	const { mutateAsync: SendEmailVerification } = usePostSendEmailVerificationApi();
	const { data: memberInfo } = useGetMemberInfoApi();
	const { mutateAsync: modifyMemberInfo } = usePostModifyMemberInfoApi();

	const handleBirthYear = (label: string) => {
		setValue('birth.year', label, { shouldValidate: true });
	};
	const handleBirthMonth = (label: string) => {
		setValue('birth.month', label, { shouldValidate: true });
	};
	const handleBirthDay = (label: string) => {
		setValue('birth.day', label, { shouldValidate: true });
	};
	const handleAddress = (val: Address) => {
		setValue(
			'address',
			{
				zipcode: val.zonecode,
				address: val.address,
				detailAddress: '',
			},
			{ shouldValidate: true }
		);
	};
	const handleSendEmailVerification = async () => {
		const res = await SendEmailVerification({ email: `${watch('email.front')}@${watch('email.back')}` });
		if (res.data.success) {
			showModal('common.alerts.emailVerificationSent');
			setIsSendEmail(true);
		}
	};
	const handleVerifyCode = async () => {
		const email = `${watch('email.front')}@${watch('email.back')}`;
		const code = watch('email.verificationCode');
		const res = await VerifyCode({ email, code });
		if (res.data.success) {
			showModal('common.alerts.verificationCompleted');
			setIsEmailVerified(true);
		} else {
			showModal('common.alerts.verificationCodeMismatch');
			setIsEmailVerified(false);
		}
	};
	const onSubmit = async (data: RegisterField) => {
		const res = await modifyMemberInfo({
			member_name: data.name,
			member_en_name: data.enName,
			member_birth_year: data.birth.year,
			member_birth_month: data.birth.month,
			member_birth_date: data.birth.day,
			member_gender: data.gender,
			member_belong: data.affiliation,
			member_zipcode: data.address.zipcode,
			member_address: data.address.address,
			member_address_detail: data.address.detailAddress,
			member_cell: data.cell.replace(/-/g, ''),
			member_email: isEmailVerified ? `${data.email.front}@${data.email.back}` : (memberInfo?.member_email ?? ''),
			member_research_field: data.researchField,
			member_student_number: data.studentNumber,
		});

		if (res.data.success) {
			openToast({
				subContent: 'common.societyName',
				content: 'common.toast.infoUpdated',
			});
		}
	};
	const convertValue = (value: string) => {
		if (value === 'm') return MYPAGE_RAW_GENDER_MALE;
		if (value === 'f') return MYPAGE_RAW_GENDER_FEMALE;
		return value;
	};

	const isEmailDomainPreset =
		!isMobile && watch('email.code') !== '' && watch('email.code') !== MYPAGE_RAW_EMAIL_DIRECT_INPUT;

	useEffect(() => {
		if (memberInfo) {
			const [emailFront = '', emailBack = ''] = memberInfo.member_email.split('@');
			setValue('name', memberInfo.member_name);
			setValue('enName', memberInfo.member_en_name);
			setValue('birth.year', memberInfo.member_birth_year);
			setValue('birth.month', memberInfo.member_birth_month);
			setValue('birth.day', memberInfo.member_birth_date);
			setValue('gender', convertValue(memberInfo.member_gender));
			setValue('address.zipcode', memberInfo.member_zipcode);
			setValue('address.address', memberInfo.member_address);
			setValue('address.detailAddress', memberInfo.member_address_detail);
			setValue('cell', memberInfo.member_cell.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3'));
			setValue('affiliation', memberInfo.member_belong ?? '');
			setValue('email.front', emailFront);
			setValue('email.back', emailBack);
			setValue('email.code', isMobile ? MYPAGE_RAW_EMAIL_DIRECT_INPUT : emailBack);
			setValue('researchField', memberInfo.member_research_field ?? '');
			setValue('studentNumber', memberInfo.member_student_number ?? '');
		}
	}, [memberInfo, isMobile, setValue]);

	useEffect(() => {
		if (!isMobile) return;
		const code = getValues('email.code');
		if (code === MYPAGE_RAW_EMAIL_DIRECT_INPUT) return;
		const back = getValues('email.back');
		const domain = back || code;
		if (domain && domain !== MYPAGE_RAW_EMAIL_DIRECT_INPUT) {
			setValue('email.back', domain, { shouldValidate: true });
		}
		setValue('email.code', MYPAGE_RAW_EMAIL_DIRECT_INPUT, { shouldValidate: true });
	}, [isMobile, getValues, setValue]);

	return (
		<S.FormContainer>
			<S.FieldBox>
				<S.Title>{t('mypage.basicInfo.title')}</S.Title>
				<Field title="common.memberType">
					<InputText value={memberInfo?.member_type ? t(memberInfo.member_type) : ''} readonly />
				</Field>
				<Field title="common.name" error={errors.name?.message ?? ''}>
					<InputText isError={!!errors.name?.message} placeholder="mypage.basicInfo.placeholders.name" {...register('name')} />
				</Field>
				<Field title="mypage.basicInfo.fields.enName" error={errors.enName?.message ?? ''}>
					<InputText isError={!!errors.enName?.message} placeholder="mypage.basicInfo.placeholders.enName" {...register('enName')} />
				</Field>
				<Field title="mypage.basicInfo.fields.birth" error={errors.birth?.year?.message ?? errors.birth?.day?.message ?? errors.birth?.month?.message ?? ''}>
					<S.FlexRow>
						<InputSelect options={yearOptions} selectedOption={watch('birth.year')} setSelectedOption={handleBirthYear} defaultValue="common.date.year" />
						<InputSelect options={monthOptions} selectedOption={watch('birth.month')} setSelectedOption={handleBirthMonth} defaultValue="common.date.month" />
						<InputSelect options={daysOptions} selectedOption={watch('birth.day')} setSelectedOption={handleBirthDay} defaultValue="common.date.day" />
					</S.FlexRow>
				</Field>
				<Field title="mypage.basicInfo.fields.gender" error={errors.gender?.message ?? ''}>
					<S.GenderBox>
						<S.GenderRadio>
							<label>
								<input type="radio" value={MYPAGE_RAW_GENDER_MALE} {...register('gender')} />
								{t('common.gender.male')}
							</label>
						</S.GenderRadio>
						<S.GenderRadio>
							<label>
								<input type="radio" value={MYPAGE_RAW_GENDER_FEMALE} {...register('gender')} />
								{t('common.gender.female')}
							</label>
						</S.GenderRadio>
					</S.GenderBox>
				</Field>
				<Field title="mypage.basicInfo.fields.affiliation" error={errors.affiliation?.message ?? ''}>
					<InputText
						isError={!!errors.affiliation?.message}
						placeholder="mypage.basicInfo.placeholders.affiliation"
						value={watch('affiliation')}
						input={(e: React.ChangeEvent<HTMLInputElement>) => setValue('affiliation', e.target.value, { shouldValidate: true })}
					/>
				</Field>
				<Field title="education.fields.researchField" error={errors.researchField?.message ?? ''}>
					<InputText
						isError={!!errors.researchField?.message}
						placeholder="education.placeholders.researchField"
						value={watch('researchField')}
						input={(e: React.ChangeEvent<HTMLInputElement>) => setValue('researchField', e.target.value, { shouldValidate: true })}
					/>
				</Field>
				<Field title="education.fields.studentNumber" error={errors.studentNumber?.message ?? ''}>
					<InputText
						isError={!!errors.studentNumber?.message}
						placeholder="education.placeholders.studentNumber"
						value={watch('studentNumber')}
						input={(e: React.ChangeEvent<HTMLInputElement>) => setValue('studentNumber', e.target.value, { shouldValidate: true })}
					/>
				</Field>
				<Field title="common.id">
					<InputText value={memberInfo?.member_id ?? ''} readonly />
				</Field>
				<Field title="mypage.basicInfo.fields.address" error={errors.address?.address?.message ?? errors.address?.detailAddress?.message ?? ''}>
					<S.AddressContentBox>
						<S.AddressBox>
							<InputText readonly {...register('address.zipcode')} />
							<S.FindButton type="button" onClick={() => setIsOpen(true)}>
								{t('common.actions.findZipcode')}
							</S.FindButton>
						</S.AddressBox>
						<S.AddressBox>
							<InputText readonly {...register('address.address')} />
						</S.AddressBox>
						<S.AddressBox>
							<InputText
								{...register('address.detailAddress')}
								placeholder="common.placeholders.detailAddress"
								isError={!!errors.address?.address?.message || !!errors.address?.detailAddress?.message}
							/>
						</S.AddressBox>
					</S.AddressContentBox>
				</Field>
				<Field title="mypage.basicInfo.fields.cell" error={errors.cell?.message ?? ''}>
					<InputText
						{...Object.assign({}, register('cell'), {
							onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
								const value = formatCell(e);
								setValue('cell', value, { shouldValidate: true });
							},
						})}
						placeholder="common.placeholders.phone"
						isError={!!errors.cell?.message}
					/>
				</Field>
				<Field title="common.emailVerification" gap="0.8rem">
					<Field error={errors.email?.back?.message ?? errors.email?.front?.message ?? errors.email?.code?.message ?? ''}>
						<S.EmailFlexRow>
							<InputText
								value={watch('email.front')}
								input={(e: React.ChangeEvent<HTMLInputElement>) => {
									setValue('email.front', e.target.value, { shouldValidate: true });
									setIsEmailVerified(false);
								}}
								isError={!!errors.email?.front?.message}
							/>
							<S.EmailAt>@</S.EmailAt>
							<InputText
								input={(e: React.ChangeEvent<HTMLInputElement>) => {
									setValue('email.back', e.target.value, { shouldValidate: true });
									setIsEmailVerified(false);
								}}
								value={isEmailDomainPreset ? watch('email.code') : watch('email.back')}
								readonly={isEmailDomainPreset}
								placeholder={isMobile ? 'common.placeholders.directInput' : undefined}
							/>
							{!isMobile && (
								<InputSelect
									options={emailOptions}
									selectedOption={watch('email.code')}
									setSelectedOption={(val) => {
										setValue('email.code', val, { shouldValidate: true });
										if (val !== MYPAGE_RAW_EMAIL_DIRECT_INPUT) setValue('email.back', val, { shouldValidate: true });
									}}
									defaultValue="common.directInput"
								/>
							)}
							<S.FindButton
								type="button"
								onClick={handleSendEmailVerification}
								disabled={
									watch('email.front') === '' ||
									watch('email.back') === '' ||
									(!isMobile && watch('email.code') === '')
								}
							>
								{t('common.actions.resend')}
							</S.FindButton>
						</S.EmailFlexRow>
					</Field>
					<S.FlexRow>
						<InputText
							{...rest}
							input={(e) => {
								onChange(e);
								setIsEmailVerified(false);
							}}
							placeholder="common.placeholders.verificationCode"
						/>
						<S.FindButton type="button" onClick={handleVerifyCode} disabled={watch('email.verificationCode') === '' || isEmailVerified}>
							{t('common.actions.verify')}
						</S.FindButton>
					</S.FlexRow>
					<S.Danger>{t('common.emailVerificationGuide')}</S.Danger>
				</Field>
			</S.FieldBox>
			<S.ButtonBox>
				<S.NextButton type="submit" onClick={handleSubmit(onSubmit)} disabled={isSendEmail && !isEmailVerified}>
					{t('common.actions.completeUpdate')}
				</S.NextButton>
			</S.ButtonBox>

			{isOpen && <InputAddress setIsOpen={setIsOpen} setAddress={handleAddress} />}
		</S.FormContainer>
	);
};

export default BasicInfo;
